import { useCallback, useMemo } from "react";
import { formatValue, asNumber } from "../../lib/kernelFormat.js";
import { CanvasSurface, KernelEmpty, resolveCssColor, colorForKey } from "./shared.jsx";

export const kind = "scatter";

export function Component({ spec, data, fillHeight = false }) {
    const points = useMemo(() => buildPoints(spec, data), [spec, data]);
    const height = Math.max(120, Math.min(420, Number(spec.height) || 220));

    const draw = useCallback((ctx, { width, height: h, root }) => {
        if (!points) return;
        const { pts, xMin, xMax, yMin, yMax, sizeMin, sizeMax } = points;
        const muted = resolveCssColor(root, "var(--plot-ink-soft)") || "#888";
        const line = resolveCssColor(root, "var(--line-soft)") || "#333";

        const padL = 44;
        const padT = 12;
        const padR = 10;
        const padB = 26;
        const gx = Math.max(1, width - padL - padR);
        const gy = Math.max(1, h - padT - padB);

        // axis frame
        ctx.strokeStyle = line;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(padL, padT);
        ctx.lineTo(padL, padT + gy);
        ctx.lineTo(padL + gx, padT + gy);
        ctx.stroke();

        // tick labels (4 each)
        ctx.fillStyle = muted;
        ctx.font = "10px ui-monospace, SFMono-Regular, Menlo, monospace";
        ctx.textBaseline = "middle";
        ctx.textAlign = "right";
        for (let i = 0; i <= 4; i++) {
            const t = i / 4;
            const yv = yMin + (yMax - yMin) * (1 - t);
            const y = padT + t * gy;
            ctx.fillText(formatTick(yv, spec.yFormat), padL - 4, y);
        }
        ctx.textBaseline = "top";
        ctx.textAlign = "center";
        for (let i = 0; i <= 4; i++) {
            const t = i / 4;
            const xv = xMin + (xMax - xMin) * t;
            const x = padL + t * gx;
            ctx.fillText(formatTick(xv, spec.xFormat), x, padT + gy + 6);
        }

        // axis labels
        if (spec.xLabel) {
            ctx.textAlign = "right";
            ctx.fillText(spec.xLabel, padL + gx, padT + gy + 16);
        }
        if (spec.yLabel) {
            ctx.save();
            ctx.translate(10, padT);
            ctx.rotate(0);
            ctx.textAlign = "left";
            ctx.textBaseline = "top";
            ctx.fillText(spec.yLabel, 0, 0);
            ctx.restore();
        }

        // points
        const xRange = xMax - xMin || 1;
        const yRange = yMax - yMin || 1;
        const sizeRange = sizeMax - sizeMin || 1;
        const baseR = 3.2;
        const maxR = 8;

        for (const p of pts) {
            const x = padL + ((p.x - xMin) / xRange) * gx;
            const y = padT + (1 - (p.y - yMin) / yRange) * gy;
            const r = p.size != null
                ? baseR + ((p.size - sizeMin) / sizeRange) * (maxR - baseR)
                : baseR;
            const c = p.series ? resolveCssColor(root, colorForKey(p.series)) : resolveCssColor(root, spec.color || "var(--green)");
            ctx.fillStyle = withAlpha(c, 0.65);
            ctx.beginPath();
            ctx.arc(x, y, r, 0, Math.PI * 2);
            ctx.fill();
            ctx.strokeStyle = withAlpha(c, 0.9);
            ctx.lineWidth = 1;
            ctx.stroke();
        }
    }, [points, spec.xFormat, spec.yFormat, spec.xLabel, spec.yLabel, spec.color]);

    if (!points || !points.pts.length) {
        return <KernelEmpty message={spec.empty || "no points"} />;
    }

    return (
        <div className={`rounded-[2px] border border-[color:var(--line)] bg-[color:var(--panel-soft)] px-[10px] py-[8px]${fillHeight ? " flex h-full min-h-0 flex-col" : ""}`}>
            {spec.title ? (
                <div className="mb-[6px] text-[10px] uppercase tracking-[0.14em] text-[color:var(--muted)]">
                    {spec.title}
                </div>
            ) : null}
            <CanvasSurface
                draw={draw}
                deps={[points, spec.xLabel, spec.yLabel, spec.color]}
                height={fillHeight ? null : height}
                className={`w-full bg-[color:var(--plot-bg)] rounded-[2px] border border-[color:var(--line-soft)]${fillHeight ? " flex-1 min-h-0" : ""}`}
            />
        </div>
    );
}

function buildPoints(spec, data) {
    const arr = Array.isArray(data) ? data : [];
    const xField = spec.xField;
    const yField = spec.yField;
    if (!xField || !yField) return null;
    const pts = [];
    let xMin = Infinity, xMax = -Infinity, yMin = Infinity, yMax = -Infinity;
    let sizeMin = Infinity, sizeMax = -Infinity;
    for (const row of arr) {
        if (!row) continue;
        const x = asNumber(row[xField]);
        const y = asNumber(row[yField]);
        if (x == null || y == null) continue;
        const size = spec.sizeField ? asNumber(row[spec.sizeField]) : null;
        const series = spec.seriesField ? row[spec.seriesField] : null;
        pts.push({ x, y, size, series });
        if (x < xMin) xMin = x;
        if (x > xMax) xMax = x;
        if (y < yMin) yMin = y;
        if (y > yMax) yMax = y;
        if (size != null) {
            if (size < sizeMin) sizeMin = size;
            if (size > sizeMax) sizeMax = size;
        }
    }
    if (!pts.length) return { pts: [] };
    if (xMin === xMax) { xMin -= 1; xMax += 1; }
    if (yMin === yMax) { yMin -= 1; yMax += 1; }
    if (sizeMin === Infinity) { sizeMin = 0; sizeMax = 1; }
    return { pts, xMin, xMax, yMin, yMax, sizeMin, sizeMax };
}

function formatTick(v, format) {
    if (format) return formatValue(v, format);
    const n = Number(v);
    if (!Number.isFinite(n)) return "";
    if (Math.abs(n) >= 1000) return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
    if (Number.isInteger(n)) return n.toString();
    return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function withAlpha(color, alpha) {
    if (!color) return color;
    if (color.startsWith("rgba(")) {
        return color.replace(/rgba\(([^)]+)\)/, (m, inner) => {
            const parts = inner.split(",").map((s) => s.trim());
            parts[3] = String(alpha);
            return `rgba(${parts.join(", ")})`;
        });
    }
    if (color.startsWith("rgb(")) {
        return color.replace("rgb(", "rgba(").replace(")", `, ${alpha})`);
    }
    if (color.startsWith("#")) {
        const r = parseInt(color.slice(1, 3), 16);
        const g = parseInt(color.slice(3, 5), 16);
        const b = parseInt(color.slice(5, 7), 16);
        if (Number.isFinite(r) && Number.isFinite(g) && Number.isFinite(b)) {
            return `rgba(${r}, ${g}, ${b}, ${alpha})`;
        }
    }
    return color;
}
